import { UserEventHandler } from '@sendbird/chat';
import type { UnreadMessageCount } from '@sendbird/chat/groupChannel';
import { usePathname } from 'expo-router';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, View } from 'smoothie';

import { SmallTileButton } from '../../common/components/GroveButtons';
import { adminUserAtom } from '../../entities/entityAtoms';
import { captureException } from '../../logger/Logger';
import { useSendbird } from '../hooks/useSendbird';

const getClientId = () => {
    if (window != null) {
        // Simple unique id for each tab/window
        window.name = `${window.name ?? new Date().toISOString()}${Math.random()}`;
        return window.name;
    }
    return 'onDevice';
};

export const MessagingButton = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const { sdk: sendbirdSdk, isLoading: isLoadingSendbird } = useSendbird();
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const canUseGuideMessaging = adminUser?.authorization.guideMessaging.canUse;
    const pathname = usePathname();
    const clientId = getClientId();

    // Initially fetch the unread message count.
    useEffect(() => {
        if (!canUseGuideMessaging) {
            return;
        }

        // Wait until we are connected to Sendbird.
        if (isLoadingSendbird || sendbirdSdk == null) {
            return;
        }

        const getUnreadCount = async () => {
            const unreadCount =
                await sendbirdSdk.groupChannel.getTotalUnreadMessageCount();
            setUnreadMessageCount(unreadCount);
        };

        getUnreadCount().catch((e) => {
            captureException(e);
        });
    }, [isLoadingSendbird, sendbirdSdk]);

    // Listen for changes to unread count across all clients,
    useEffect(() => {
        if (!canUseGuideMessaging) {
            return;
        }

        // Wait until we are connected to Sendbird.
        if (isLoadingSendbird || sendbirdSdk == null) {
            return;
        }

        // The UNIQUE_HANDLER_ID is a unique identifier you should declare to register multiple concurrent handlers.
        // Include clientId to ensure uniqueness across each tab/window/device, and pathname to ensure uniqueness for this button on each page
        const UNIQUE_HANDLER_ID = `${clientId}-${pathname.replace('/', '')}-mb-onTotalUnreadMessageCountChanged-${adminUser.id}`;

        const userEventHandler = new UserEventHandler({
            onTotalUnreadMessageCountChanged: (
                unreadMessageCount: UnreadMessageCount,
            ) => {
                setUnreadMessageCount(unreadMessageCount.groupChannelCount);
            },
        });

        sendbirdSdk.addUserEventHandler(UNIQUE_HANDLER_ID, userEventHandler);

        return () => {
            sendbirdSdk.removeUserEventHandler(UNIQUE_HANDLER_ID);
        };
    }, [isLoadingSendbird, sendbirdSdk]);

    if (!canUseGuideMessaging) {
        return null;
    }

    return (
        <View>
            <SmallTileButton
                icon={MessageSquareIcon}
                label="Messaging"
                href="/guide-messaging"
            />
            {/* Optionally could display the unread count, instead of a generic boolean indicator. */}
            {unreadMessageCount > 0 && (
                <View
                    position="absolute"
                    top={4}
                    right={22}
                    height={10}
                    width={10}
                    borderRadius={100}
                    backgroundColor="$red900"
                />
            )}
        </View>
    );
};
